import React, { useCallback, useEffect, useState } from 'react'
import { SubscriptionSkeleton } from '../../components/Skeleton'
import {
  BackLink,
  Card,
  CardTitle,
  ContentMaxWidth,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  PrimaryButton,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { usePatient } from '../../context/PatientContext'
import * as svc from '../../services/shopService'
import { parseUtcDate } from '../../utils/dates'

export default function SubscriptionPage() {
  const { currentProfile } = usePatient()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(null)

  const refresh = useCallback(async () => {
    if (!currentProfile) return
    setLoading(true)
    try {
      const sub = await svc.getSubscription(currentProfile)
      setData(sub)
    } finally {
      setLoading(false)
    }
  }, [currentProfile])

  useEffect(() => {
    setData(null)
    refresh()
  }, [refresh])

  async function upgrade(tierId) {
    setBusy(tierId)
    try {
      await svc.upgradeSubscription(currentProfile, tierId)
      await refresh()
    } finally {
      setBusy(null)
    }
  }

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/shop">Back to Shop</BackLink>
        <div>
          <Pill>Shop</Pill>
          <PageHeading style={{ marginTop: 12 }}>Subscription</PageHeading>
          <PageSubheading>Your current tier and upgrade options.</PageSubheading>
        </div>

        {loading && !data ? (
          <SubscriptionSkeleton />
        ) : (
          <>
            <Card>
              <Row $between>
                <div>
                  <CardTitle style={{ margin: 0 }}>Current tier</CardTitle>
                  <Muted style={{ marginTop: 4 }}>
                    {data?.tier || 'Free'}
                    {data?.renewsAt
                      ? ` · renews ${parseUtcDate(data.renewsAt).toLocaleDateString()}`
                      : ''}
                  </Muted>
                </div>
                <Pill>{data?.tier || 'Free'}</Pill>
              </Row>
            </Card>

            {data?.upgradeOptions?.length > 0 && (
              <Stack $gap="10px">
                <h3
                  style={{
                    margin: 0,
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#6B7280',
                  }}
                >
                  Upgrade
                </h3>
                {data.upgradeOptions.map((opt) => (
                  <Card key={opt.tierId}>
                    <Row $between $wrap $gap="12px">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <CardTitle style={{ marginBottom: 4 }}>{opt.name}</CardTitle>
                        <Muted style={{ marginBottom: 8 }}>{opt.priceLabel}</Muted>
                        <ul style={{ margin: 0, paddingLeft: 20, color: '#4B5563', fontSize: 14 }}>
                          {(opt.features || []).map((f, i) => (
                            <li key={i}>{f}</li>
                          ))}
                        </ul>
                      </div>
                      <PrimaryButton
                        onClick={() => upgrade(opt.tierId)}
                        disabled={busy === opt.tierId}
                      >
                        {busy === opt.tierId ? 'Working…' : `Upgrade to ${opt.name}`}
                      </PrimaryButton>
                    </Row>
                  </Card>
                ))}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </ContentMaxWidth>
  )
}
